/**
 * applyViewConfig — filter + reorder a module's antd columns by a ViewConfig,
 * and synthesize a default column for any visible registry field that has no
 * bespoke column in the List page (Phase 5).
 * buildQuery — union a fetch baseline with the fields/populate of visible
 * columns for server-side (Hybrid) narrowing.
 *
 * For bespoke columns neither touches `render`; only which columns appear and
 * in what order. Columns flagged `alwaysOn` in the registry are always kept.
 */

import type { ReactNode } from "react"
import { Tag } from "antd"
import { t } from "@/languages/i18n"
import { formatDatePost } from "@/utils/formatDate"
import { toImageSrc } from "@/lib/utils"
import { LocaleLabel } from "@/(admin)/components/locale-label"
import type { ColumnMeta, ColumnType, ViewConfig, ViewConfigQuery } from "./types"

/** A column carrying an optional `key` we can match against the registry. */
type KeyedColumn = { key?: string | number | bigint }

/** Minimal antd-compatible column shape the engine synthesizes for a field. */
type SynthColumn = {
  key: string
  title: string
  dataIndex?: (string | number)[]
  render: (value: unknown, record: unknown, index: number) => ReactNode
  ellipsis: boolean
  width: number
  sorter?: boolean
  showSorterTooltip?: boolean
}

/** Columns kept multi-capable (interactive/icon cells) — never force-truncated. */
const ELLIPSIS_SKIP_KEYS = new Set(["action", "bulk"])

/**
 * Default pixel width per render family for a synthesized (added) column, so a
 * column enabled from "Configure view" keeps a readable size with its text on a
 * single line instead of collapsing. Overridable per column via `ColumnMeta.width`.
 */
const DEFAULT_WIDTH_BY_TYPE: Record<ColumnType, number> = {
  text: 180,
  number: 120,
  date: 170,
  bool: 110,
  rel: 170,
  relList: 220,
  media: 170,
  locale: 140,
}

/** Fallback width (px) for a column with no numeric width and no known type. */
const FALLBACK_COLUMN_WIDTH = 180

/** The px width the auto-scaling layout should reserve for a column. Numeric
 *  widths win; `"120px"` strings are parsed; `%`/absent fall back to the
 *  registry `width`, then the type default, then {@link FALLBACK_COLUMN_WIDTH}. */
function columnWidthPx(col: { width?: unknown }, meta?: ColumnMeta): number {
  const w = col.width
  if (typeof w === "number") return w
  if (typeof w === "string") {
    const px = /^(\d+(?:\.\d+)?)px$/.exec(w.trim())
    if (px) return Math.round(Number(px[1]))
  }
  if (meta?.width) return meta.width
  if (meta?.type) return DEFAULT_WIDTH_BY_TYPE[meta.type] ?? FALLBACK_COLUMN_WIDTH
  return FALLBACK_COLUMN_WIDTH
}

/**
 * Sum the pixel widths of the displayed columns → the value to pass as
 * `scroll={{ x }}`. When more columns are shown than fit, the table scrolls
 * horizontally (each column keeps its width, content stays on one line) instead
 * of squeezing every cell. Pass the columns already returned by
 * {@link applyViewConfig}; `registry` lets width fall back to `type` defaults.
 */
export function columnScrollX(
  columns: readonly { key?: string | number | bigint; width?: unknown }[],
  registry: ColumnMeta[] = [],
): number {
  const metaByKey = new Map(registry.map(m => [m.key, m]))
  return columns.reduce((sum, col) => {
    const meta = col.key != null ? metaByKey.get(String(col.key)) : undefined
    return sum + columnWidthPx(col, meta)
  }, 0)
}

/** Muted em-dash for empty cells — matches the bespoke columns' "—". */
const dash = (): ReactNode => <span style={{ color: "var(--admin-muted-fg)" }}>—</span>

/** Unwrap a Strapi relation value to its attribute bag, tolerating v4 `{ data:
 *  { attributes } }`, `{ data: null }`, and already-flat objects. */
function relEntity(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null
  let node: unknown = value
  if ("data" in (node as Record<string, unknown>)) node = (node as { data: unknown }).data
  if (!node || typeof node !== "object") return null
  const obj = node as Record<string, unknown>
  if (obj.attributes && typeof obj.attributes === "object") return obj.attributes as Record<string, unknown>
  return obj
}

/** Normalize a relation-list value to an array of attribute bags. */
function relArray(value: unknown): Record<string, unknown>[] {
  if (!value || typeof value !== "object") return []
  let arr: unknown = value
  if ("data" in (value as Record<string, unknown>)) arr = (value as { data: unknown }).data
  if (arr == null) return []
  const list = Array.isArray(arr) ? arr : [arr]
  return list
    .map(item => {
      if (!item || typeof item !== "object") return null
      const obj = item as Record<string, unknown>
      return (obj.attributes && typeof obj.attributes === "object")
        ? (obj.attributes as Record<string, unknown>)
        : obj
    })
    .filter((x): x is Record<string, unknown> => x != null)
}

/** Pick the display name of a relation from common label fields. */
function relName(attrs: Record<string, unknown> | null): string | null {
  if (!attrs) return null
  const v = attrs.name ?? attrs.username ?? attrs.title ?? attrs.position ?? attrs.fullname
  return v == null || v === "" ? null : String(v)
}

/** Human-readable file size. Strapi upload `size` is in KB. */
function humanSize(value: unknown): string | null {
  const kb = Number(value)
  if (!Number.isFinite(kb)) return null
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * Default renderers for engine-synthesized columns, keyed by `ColumnMeta.type`.
 * Relation/media renderers tolerate un-populated data → "—" (never crash, never
 * `[object Object]`).
 */
export const renderByType: Record<ColumnType, (value: unknown) => ReactNode> = {
  text: value => (value == null || value === "" ? dash() : <span>{String(value)}</span>),
  number: value => {
    if (value == null || value === "") return dash()
    const n = Number(value)
    return Number.isFinite(n) ? <span>{n.toLocaleString()}</span> : dash()
  },
  date: value => (value ? <span>{formatDatePost(String(value))}</span> : dash()),
  bool: value =>
    value ? <Tag color="green">{t("Có")}</Tag> : <Tag>{t("Không")}</Tag>,
  rel: value => {
    const name = relName(relEntity(value))
    return name ? <span>{name}</span> : dash()
  },
  relList: value => {
    const names = relArray(value)
      .map(relName)
      .filter((x): x is string => x != null)
    if (!names.length) return dash()
    const shown = names.slice(0, 3).join(", ")
    return <span>{names.length > 3 ? `${shown} +${names.length - 3}` : shown}</span>
  },
  media: value => {
    // Handle both single media (`{ data: {...} }`) and multi media
    // (`{ data: [...] }`): render the first file as a link, with "+N" when
    // there are more. `relArray` normalizes every shape to attribute bags, so a
    // populated multi-image field no longer collapses to "—".
    const items = relArray(value)
    const first = items.find(a => a?.url)
    if (!first?.url) return dash()
    const extra = items.length > 1 ? ` +${items.length - 1}` : ""
    return (
      <a href={toImageSrc(String(first.url))} target="_blank" rel="noreferrer">
        {first.name ? String(first.name) : t("Tệp")}
        {extra}
      </a>
    )
  },
  locale: value => (value ? <LocaleLabel locale={String(value)} /> : dash()),
}

/** Pick the render for a synthesized column, with a media-size nicety. */
function renderForMeta(meta: ColumnMeta): (value: unknown) => ReactNode {
  const type: ColumnType = meta.type ?? "text"
  if (type === "number" && meta.key === "size") {
    return value => {
      const s = humanSize(value)
      return s ? <span>{s}</span> : dash()
    }
  }
  return renderByType[type]
}

/** Build the antd column the List page never declared for a registry field. */
function synthesizeColumn(meta: ColumnMeta): SynthColumn {
  return {
    key: meta.key,
    title: meta.title,
    dataIndex: meta.dataIndex,
    render: (value, _record, _index) => renderForMeta(meta)(value),
    ellipsis: true,
    // A fixed px width keeps the added column readable (content on one line) and
    // lets the table scroll horizontally instead of squeezing existing columns.
    width: meta.width ?? (meta.type ? DEFAULT_WIDTH_BY_TYPE[meta.type] : FALLBACK_COLUMN_WIDTH),
    // A registry field flagged `sortable` gets a server-side sorter just like a
    // bespoke column — the List page's existing onChange already derives the
    // Strapi `sort` field from this column's `dataIndex`. Without this, every
    // column added via "Configure view" silently had no working sort.
    ...(meta.sortable ? { sorter: true, showSorterTooltip: false } : {}),
  }
}

/**
 * Force a column to a single line with an ellipsis so cells never wrap onto a
 * second line when many columns are shown. Respects a column's own `ellipsis`
 * and leaves action/interactive columns untouched.
 */
function withEllipsis<C extends KeyedColumn>(col: C, key: string | undefined): C {
  if (key && ELLIPSIS_SKIP_KEYS.has(key)) return col
  if ((col as { ellipsis?: unknown }).ellipsis !== undefined) return col
  return { ...col, ellipsis: true }
}

/**
 * Return `allColumns` filtered to the visible set and ordered by `config.order`.
 * `alwaysOn` columns are always included. A registry field that is visible but
 * has no bespoke column is synthesized from its `type` + `dataIndex` (Phase 5).
 * Columns whose key is absent from the registry are appended in original order
 * (defensive — a registry should list every bespoke column).
 */
export function applyViewConfig<C extends KeyedColumn>(
  allColumns: readonly C[],
  registry: ColumnMeta[],
  config: ViewConfig,
): C[] {
  const metaByKey = new Map(registry.map(m => [m.key, m]))
  const colByKey = new Map<string, C>()
  const unregistered: C[] = []

  for (const col of allColumns) {
    const key = col.key != null ? String(col.key) : undefined
    if (key && metaByKey.has(key)) {
      colByKey.set(key, col)
    } else {
      unregistered.push(col)
    }
  }

  const visible = new Set(config.visibleKeys)
  const result: C[] = []

  for (const key of config.order) {
    const meta = metaByKey.get(key)
    const isOn = meta?.alwaysOn || visible.has(key)
    if (!isOn) continue
    const col = colByKey.get(key)
    if (col) {
      result.push(withEllipsis(col, key))
    } else if (meta) {
      // No bespoke column for this field — build a default one from its type.
      result.push(synthesizeColumn(meta) as unknown as C)
    }
  }

  // Keep any columns the registry didn't declare (safety net).
  const keptUnregistered = unregistered.map(col =>
    withEllipsis(col, col.key != null ? String(col.key) : undefined),
  )
  return [...result, ...keptUnregistered]
}

/** Shallow-clone a plain object so we never mutate the caller's baseline. */
function clonePopulate(src: Record<string, unknown>): Record<string, unknown> {
  return { ...src }
}

/**
 * Build the Strapi `{ fields, populate }` for a fetch: the baseline (fields the
 * list always needs — rowKey, alwaysOn columns) unioned with the queryFields /
 * queryPopulate of every currently-visible column.
 *
 * When every column is visible (localStorage empty) the result is a superset of
 * the baseline, so behaviour is unchanged; hiding a column drops its fields.
 * Relation fields never populate unless their column is on — never `populate:'*'`.
 */
export function buildQuery(
  registry: ColumnMeta[],
  config: ViewConfig,
  baseline: ViewConfigQuery,
): ViewConfigQuery {
  const fields = new Set(baseline.fields)
  const populate: Record<string, unknown> = clonePopulate(baseline.populate)
  const visible = new Set(config.visibleKeys)

  for (const meta of registry) {
    const isOn = meta.alwaysOn || visible.has(meta.key)
    if (!isOn) continue
    for (const f of meta.queryFields ?? []) fields.add(f)
    if (meta.queryPopulate) {
      for (const [rel, cfg] of Object.entries(meta.queryPopulate)) {
        populate[rel] = cfg
      }
    }
  }

  return { fields: [...fields], populate }
}
